import { useCallback, useEffect, useMemo, useState } from "react";
import { Boxes, DatabaseZap, Layers3, LockKeyhole, RefreshCw, SlidersHorizontal } from "lucide-react";
import type { TenantClassification, TenantListView } from "@ubisafe/shared";
import { TenantClassificationBadge, tenantClassificationLabels } from "../components/TenantClassificationBadge";
import { api, ApiClientError } from "../lib/api";

const classificationOrder: TenantClassification[] = ["CLIENT", "POC", "TRIAL", "IGNORED"];
type CatalogFilter = TenantClassification | "ALL";

export function TenantCatalogPage() {
  const [data, setData] = useState<TenantListView | null>(null);
  const [filter, setFilter] = useState<CatalogFilter>("ALL");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await api.listTenantCatalog());
    } catch (loadError) {
      setError(loadError instanceof ApiClientError ? loadError.message : "Não foi possível carregar os tenants.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(() => {
    const initial: Record<TenantClassification, number> = { CLIENT: 0, POC: 0, TRIAL: 0, IGNORED: 0 };
    return (data?.tenants ?? []).reduce((result, tenant) => {
      result[tenant.classification] += 1;
      return result;
    }, initial);
  }, [data]);

  const visibleTenants = useMemo(
    () => data?.tenants.filter((tenant) => filter === "ALL" || tenant.classification === filter) ?? [],
    [data, filter],
  );

  const updateClassification = async (identifier: string, classification: TenantClassification) => {
    setSaving(identifier);
    setError("");
    try {
      const updated = await api.updateTenantClassification(identifier, classification);
      setData((current) =>
        current
          ? {
              ...current,
              tenants: current.tenants.map((tenant) => (tenant.identifier === identifier ? updated : tenant)),
            }
          : current,
      );
    } catch (updateError) {
      setError(updateError instanceof ApiClientError ? updateError.message : "Não foi possível alterar a classificação.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="page-stack catalog-page">
      <section className="page-heading page-heading--split">
        <div>
          <span className="eyebrow">Administração</span>
          <h1>Classificação de tenants</h1>
          <p>Todo tenant é Cliente por padrão. Registre apenas as exceções POC, Trial ou Ignorado.</p>
        </div>
        <div className="catalog-total">
          <Layers3 aria-hidden="true" />
          <div><strong>{data?.tenants.length ?? 0}</strong><span>tenants no snapshot</span></div>
        </div>
      </section>

      {data?.source === "CONTROLLED" && (
        <div className="controlled-data-banner" role="note">
          <DatabaseZap aria-hidden="true" />
          <div>
            <strong>Catálogo de validação</strong>
            <span>As classificações são persistidas; os dados de origem continuam sintéticos nesta versão.</span>
          </div>
        </div>
      )}

      {error && (
        <div className="inline-alert inline-alert--error" role="alert">
          <span>{error}</span>
          {!data && <button onClick={() => void load()}>Tentar novamente</button>}
        </div>
      )}

      <section className="classification-filters" aria-label="Filtrar tenants por classificação">
        <div className="classification-filters__label"><SlidersHorizontal aria-hidden="true" />Exibir</div>
        <button className={filter === "ALL" ? "filter-chip filter-chip--active" : "filter-chip"} onClick={() => setFilter("ALL")}>
          Todos <strong>{data?.tenants.length ?? 0}</strong>
        </button>
        {classificationOrder.map((classification) => (
          <button
            key={classification}
            className={filter === classification ? `filter-chip filter-chip--active filter-chip--${classification.toLowerCase()}` : "filter-chip"}
            onClick={() => setFilter(classification)}
          >
            {tenantClassificationLabels[classification]} <strong>{counts[classification]}</strong>
          </button>
        ))}
      </section>

      {loading && (
        <div className="tenant-grid" aria-label="Carregando tenants" aria-busy="true">
          {Array.from({ length: 8 }, (_, index) => <div className="tenant-card tenant-card--skeleton" key={index} />)}
        </div>
      )}

      {!loading && !data && (
        <div className="state-panel state-panel--error">
          <RefreshCw aria-hidden="true" />
          <h2>Catálogo indisponível</h2>
          <p>Use “Tentar novamente” para consultar o snapshot.</p>
        </div>
      )}

      {!loading && data && (
        <div className="tenant-grid tenant-grid--catalog" aria-live="polite">
          {visibleTenants.map((tenant) => (
            <article className={`tenant-card tenant-card--${tenant.classification.toLowerCase()}`} key={tenant.identifier}>
              <div className="tenant-card__header">
                <TenantClassificationBadge classification={tenant.classification} />
                <span className="tenant-card__identifier">#{tenant.identifier}</span>
              </div>
              <div className="tenant-card__identity">
                <h2>{tenant.name}</h2>
                <span>{tenant.classification === "CLIENT" ? "Tenant de faturamento" : `Ambiente ${tenantClassificationLabels[tenant.classification]}`}</span>
              </div>
              {tenant.classification === "CLIENT" && (
                <div className="tenant-card__compact-metric">
                  <Boxes aria-hidden="true" />
                  <strong>{tenant.activePatrimonies.toLocaleString("pt-BR")}</strong>
                  <span>patrimônios ativos</span>
                </div>
              )}
              <label className="classification-select">
                <span>Classificação</span>
                <select
                  value={tenant.classification}
                  disabled={saving === tenant.identifier || tenant.hasActivePoc}
                  aria-label={`Classificação de ${tenant.name}`}
                  aria-describedby={tenant.hasActivePoc ? `classification-lock-${tenant.identifier}` : undefined}
                  onChange={(event) => void updateClassification(tenant.identifier, event.target.value as TenantClassification)}
                >
                  {classificationOrder.map((classification) => (
                    <option key={classification} value={classification}>{tenantClassificationLabels[classification]}</option>
                  ))}
                </select>
                {saving === tenant.identifier && <small>Salvando…</small>}
                {tenant.hasActivePoc && (
                  <small className="classification-lock" id={`classification-lock-${tenant.identifier}`}>
                    <LockKeyhole aria-hidden="true" /> Conclua a limpeza antes de reclassificar
                  </small>
                )}
              </label>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
